import type { DeckProject } from './types'

export type RecoveryReason = 'autosave' | 'manual' | 'visibility'

export interface RecoverySnapshot {
  id: string
  projectId: string
  projectName: string
  projectUpdatedAt: string
  createdAt: string
  reason: RecoveryReason
  byteSize: number
  project: DeckProject
}

export interface PendingProjectSave {
  projectId: string
  projectName: string
  projectUpdatedAt: string
  startedAt: string
  byteSize: number
  project: DeckProject
}

export interface RecoveryCandidate {
  pending: PendingProjectSave
  savedProject: DeckProject | null
  savedProjectDamaged: boolean
}

export const RECOVERY_SNAPSHOT_INTERVAL_MS = 5 * 60 * 1_000

export const approximateProjectBytes = (project: DeckProject) =>
  new TextEncoder().encode(JSON.stringify(project)).byteLength

export const recoverySnapshotLimit = (projectBytes: number) => {
  if (projectBytes > 100 * 1024 * 1024) return 1
  if (projectBytes > 25 * 1024 * 1024) return 3
  return 5
}

export const retainedRecoverySnapshots = (snapshots: RecoverySnapshot[], projectBytes: number) =>
  [...snapshots]
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
    .slice(0, recoverySnapshotLimit(projectBytes))

export const shouldCreateRecoverySnapshot = (
  previous: RecoverySnapshot | undefined,
  project: DeckProject,
  now = Date.now(),
) => {
  if (!previous) return true
  if (previous.projectUpdatedAt === project.updatedAt) return false
  return now - Date.parse(previous.createdAt) >= RECOVERY_SNAPSHOT_INTERVAL_MS
}

export const shouldOfferPendingSave = (
  pending: PendingProjectSave,
  savedProject: DeckProject | null,
  savedProjectDamaged = false,
) => savedProjectDamaged || !savedProject || Date.parse(pending.projectUpdatedAt) > Date.parse(savedProject.updatedAt)

export const isSavedRevisionCurrent = (currentProject: DeckProject | null, savedUpdatedAt: string) =>
  currentProject?.updatedAt === savedUpdatedAt

export const recoveryReasonLabel = (reason: RecoveryReason) => {
  if (reason === 'manual') return 'Manual recovery copy'
  if (reason === 'visibility') return 'Saved when Deckelier was hidden'
  return 'Automatic recovery copy'
}

